import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { Eye, EyeOff, Trash2, X } from "lucide-react";

import { listIntegrations, type Integration } from "@/api/integrations";
import {
  getMetadataEntry,
  getMetadataStats,
  listMetadataEntries,
  purgeMetadataEntry,
  type MetadataEntry,
  type MetadataStatus,
} from "@/api/metadata";
import { ConfirmModal } from "@/components/ConfirmModal";
import { MenuItem } from "@/components/KebabMenu";
import { ErrorBox, RefreshFailed, Spinner, loadFailure } from "@/components/Spinner";
import {
  Button,
  EmptyState,
  Page,
  PageHeader,
  SectionTabs,
  StatusPill,
  TabPanel,
  Table,
  type TableColumn,
} from "@/components/ui";
import { formatDateTime, fromEpochSeconds } from "@/lib/dates";

/**
 * Cache d'enrichissement — ce que le référentiel musical sait, titre par titre.
 *
 * Deux partitions, jamais fusionnées : les titres reconnus et ceux que le
 * fournisseur ignore. L'index n'a pas d'ordre commun aux deux, et une liste
 * mélangée rendrait une pagination fausse — d'où deux onglets et non un filtre
 * facultatif.
 *
 * Une entrée est désignée par sa **référence d'enregistrement** — un condensé
 * illisible, mais le seul identifiant qu'une reconnaissance garantisse. C'est
 * pour ça que le titre, l'artiste et l'ISRC occupent les premières colonnes et
 * que la référence est reléguée : elle sert à agir, pas à lire.
 */

const PAGE_SIZE = 50;

/** The API dates in seconds; the formatter takes milliseconds. */
function formatDate(epochSeconds: number | null): string {
  return formatDateTime(fromEpochSeconds(epochSeconds));
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Un opérateur ne connaît pas nos clés de source — mais il doit voir d'un coup
 * d'œil si une fiche a coûté quelque chose. Le libellé le dit, la couleur le
 * répète.
 *
 * Les deux viennent du **catalogue backend**, jamais d'une table écrite ici :
 * cette page en tenait une, « Services tiers » en tenait une autre, et elles
 * ne disaient déjà plus la même chose. Même clé de requête que la page des
 * services tiers, donc une seule requête pour les deux écrans.
 */
function useProviders(): Record<string, Integration> {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const { data } = useQuery({
    queryKey: ["integrations"],
    queryFn: () => listIntegrations(token),
    enabled: Boolean(token),
  });
  return Object.fromEntries(
    (data?.items ?? []).map((item) => [item.provider, item]),
  );
}

function sourceLabel(
  providers: Record<string, Integration>,
  source: string,
): string {
  // Repli sur la clé brute : une source que le backend ne décrit pas encore
  // doit rester lisible plutôt que disparaître.
  return providers[source]?.label ?? source;
}

function SourceBadge({
  providers,
  source,
}: {
  providers: Record<string, Integration>;
  source: string;
}) {
  const known = providers[source];
  return (
    <StatusPill
      tone={known?.billing === "metered" ? "warning" : "neutral"}
      dot={false}
      title={known?.purpose}
    >
      {known?.label ?? source}
    </StatusPill>
  );
}

const TABS_ID = "metadata-status";

const TABS: { value: MetadataStatus; label: string; help: string }[] = [
  {
    value: "hit",
    label: "Recognized tracks",
    help: "Kept indefinitely — a released track's rights holders don't change. \"Forget\" is the only way to refresh it.",
  },
  {
    value: "miss",
    label: "Unknown tracks",
    help: "Retried after a week — a track missing today may be added tomorrow. All we know about them is their reference: that's all recognition returned.",
  },
];

function EntryDetail({ acrid, onClose }: { acrid: string; onClose: () => void }) {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const query = useQuery({
    queryKey: ["metadata-entry", acrid],
    queryFn: () => getMetadataEntry(token, acrid),
    enabled: !!token,
  });
  const providers = useProviders();

  // Le panneau se monte SOUS la pagination, hors viewport dès que la liste
  // remplit l'écran — et le bandeau des tâches mange le bas. Sans ce scroll,
  // « Détail » a toutes les apparences d'un bouton mort (constaté le
  // 2026-08-03). Rejoué à chaque changement d'acrid : cliquer le détail
  // d'une autre ligne doit ramener le panneau en vue aussi.
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [acrid]);

  return (
    <div className="card mt-4" ref={panelRef}>
      <div className="mb-3 flex items-center justify-between">
        {/* La référence complète, sans troncature : c'est ici qu'on vient la
            chercher pour la recoller dans la console du fournisseur. */}
        <h2 className="font-mono text-sm font-semibold text-ink">
          {acrid}
        </h2>
        <Button
          variant="ghost"
          size="sm"
          icon={X}
          onClick={onClose}
          aria-label="Close details"
        >
          Close
        </Button>
      </div>
      {query.isLoading && <Spinner label="Loading record…" />}
      {query.error && <ErrorBox error={query.error} />}
      {query.data && (
        <div className="space-y-4">
          {Object.entries(query.data.providers ?? {}).map(([source, entry]) => (
            <div key={source}>
              <div className="mb-1 flex items-baseline gap-2">
                <SourceBadge providers={providers} source={source} />
                <span className="text-xs text-ink-muted">
                  matched by {entry.matched_by} · {formatDate(entry.fetched_at)}
                </span>
              </div>
              {/* La fiche est rendue telle que le fournisseur l'a donnée. On
                  ne la met pas en forme : la question qu'un opérateur se pose
                  devant cet écran est « qu'est-ce qui a été reçu, exactement ». */}
              <pre className="max-h-96 overflow-auto rounded bg-sunken p-3 text-xs text-ink">
                {JSON.stringify(entry.record, null, 2)}
              </pre>
            </div>
          ))}
          {Object.entries(query.data.misses ?? {}).map(([source, miss]) => (
            // Pourquoi un fournisseur n'a pas été re-interrogé : la question
            // qui vient juste après « pourquoi ce titre n'est pas enrichi ».
            <p key={source} className="text-xs text-ink-muted">
              {sourceLabel(providers, source)} didn't know this
              recording on{" "}
              {formatDate(miss.at)} — it will be retried after{" "}
              {formatDate(miss.expires_at)}.
            </p>
          ))}
          {Object.keys(query.data.providers ?? {}).length === 0 &&
            Object.keys(query.data.misses ?? {}).length === 0 && (
              <EmptyState size="inline" title="No record.">
                This entry has nothing on file from a provider.
              </EmptyState>
            )}
        </div>
      )}
    </div>
  );
}

export function MetadataRecordingsPage() {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<MetadataStatus>("hit");
  // Pile des curseurs déjà traversés : le dernier sert la page courante, et en
  // dépiler un revient en arrière. DynamoDB ne sait pas paginer à rebours.
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const [selected, setSelected] = useState<string | null>(null);
  const [toPurge, setToPurge] = useState<MetadataEntry | null>(null);

  const cursor = cursors[cursors.length - 1];

  const stats = useQuery({
    queryKey: ["metadata-stats"],
    queryFn: () => getMetadataStats(token),
    enabled: !!token,
  });

  const page = useQuery({
    queryKey: ["metadata-entries", status, cursor],
    queryFn: () => listMetadataEntries(token, status, cursor, PAGE_SIZE),
    enabled: !!token,
  });

  const purge = useMutation({
    mutationFn: (acrid: string) => purgeMetadataEntry(token, acrid),
    onSuccess: () => {
      setToPurge(null);
      setSelected(null);
      queryClient.invalidateQueries({ queryKey: ["metadata-entries"] });
      queryClient.invalidateQueries({ queryKey: ["metadata-stats"] });
    },
  });

  function switchStatus(next: MetadataStatus) {
    setStatus(next);
    // Un curseur appartient à UNE partition : le garder en changeant d'onglet
    // demanderait à DynamoDB de reprendre une liste dans une autre.
    setCursors([null]);
    setSelected(null);
  }

  const tab = TABS.find((t) => t.value === status)!;

  const columns: TableColumn<MetadataEntry>[] = [
    { key: "title", header: "Track", cell: (entry) => entry.title ?? "—" },
    { key: "artist", header: "Artist", cell: (entry) => entry.artist ?? "—" },
    // Facultatif même sur un titre reconnu : le référentiel n'attribue pas
    // d'ISRC à tout ce qu'il connaît.
    { key: "isrc", header: "ISRC", mono: true, cell: (entry) => entry.isrc ?? "—" },
    {
      key: "reference",
      header: "Reference",
      mono: true,
      muted: true,
      cell: (entry) => <span title={entry.acrid}>{entry.acrid.slice(0, 10)}…</span>,
    },
    { key: "resolved", header: "Resolved on", muted: true, cell: (entry) => formatDate(entry.fetchedAt) },
    // Un titre reconnu n'expire plus (2026-08-03) : la colonne n'a de sens que
    // sur les entrées négatives.
    ...(status === "miss"
      ? [
          {
            key: "expires",
            header: "Expires on",
            muted: true,
            cell: (entry: MetadataEntry) => formatDate(entry.expiresAt),
          },
        ]
      : []),
  ];

  return (
    <Page>
      <PageHeader
        title="Metadata"
        subtitle="What the music reference database has already returned, track by track. Every missing entry costs a call to the provider — this page exists to see what's been paid for, and what's missing."
      />

      {stats.data && (
        <div className="card mb-6 max-w-md">
          <div className="flex gap-8 text-sm">
            <div>
              <div className="text-2xl font-semibold text-ink">
                {stats.data.approximateEntries.toLocaleString("en-US")}
              </div>
              <div className="text-ink-muted">cached tracks</div>
            </div>
            <div>
              <div className="text-2xl font-semibold text-ink">
                {formatBytes(stats.data.sizeBytes)}
              </div>
              <div className="text-ink-muted">volume</div>
            </div>
          </div>
          <p className="mt-3 text-xs text-ink-muted">
            Figures refreshed by AWS every{" "}
            {stats.data.refreshedByAwsEveryHours} hours or so — an order of
            magnitude, not an exact count.
          </p>
        </div>
      )}

      {/* Manual activation: selecting a tab asks the server for its list, so the arrows only move the
          focus and Enter / Space select — sweeping the bar costs no request. */}
      <SectionTabs
        label="Cached tracks"
        idPrefix={TABS_ID}
        activation="manual"
        tabs={TABS}
        value={status}
        onChange={switchStatus}
      />

      {/* One body whose content follows the tab: it is labelled by the tab that is current. */}
      <TabPanel idPrefix={TABS_ID} value={status}>
        <p className="my-4 text-sm text-ink-muted">{tab.help}</p>

        <RefreshFailed query={page} className="mb-4" />
        <Table
          label={tab.label}
          columns={columns}
          rows={page.data?.items}
          rowKey={(entry) => entry.acrid}
          error={loadFailure(page)}
          empty={<EmptyState title={status === "hit" ? "No recognized tracks yet" : "No unknown tracks yet"} />}
          actions={{
            label: (entry) => `Actions for ${entry.acrid}`,
            items: (entry) => (
              <>
                {entry.status === "hit" && (
                  <MenuItem
                    icon={selected === entry.acrid ? EyeOff : Eye}
                    onClick={() => setSelected(selected === entry.acrid ? null : entry.acrid)}
                  >
                    {selected === entry.acrid ? "Hide details" : "Details"}
                  </MenuItem>
                )}
                <MenuItem danger icon={Trash2} onClick={() => setToPurge(entry)}>
                  Forget…
                </MenuItem>
              </>
            ),
          }}
        />

        {/* Also while a later page loads or failed: "Previous page" is the way back, and the table above
            already says what happened (a failed page keeps its Retry). */}
        {(page.data || cursors.length > 1) && (
          <div className="mt-4 flex gap-2">
            <Button
              variant="secondary"
              disabled={cursors.length === 1}
              onClick={() => setCursors((c) => c.slice(0, -1))}
            >
              Previous page
            </Button>
            <Button
              variant="secondary"
              disabled={!page.data?.nextCursor}
              onClick={() =>
                setCursors((c) => [...c, page.data?.nextCursor ?? null])
              }
            >
              Next page
            </Button>
          </div>
        )}

        {selected && (
          <EntryDetail acrid={selected} onClose={() => setSelected(null)} />
        )}
      </TabPanel>

      {toPurge && (
        <ConfirmModal
          title="Forget this entry?"
          confirmLabel="Forget"
          pendingLabel="Deleting…"
          pending={purge.isPending}
          danger
          error={purge.error}
          onCancel={() => {
            purge.reset();
            setToPurge(null);
          }}
          onConfirm={() => purge.mutate(toPurge.acrid)}
        >
          <p>
            {/* Un « miss » n'a ni titre ni ISRC — il ne reste que la
                référence, illisible mais non ambiguë. */}
            <strong>{toPurge.title ?? toPurge.isrc ?? toPurge.acrid}</strong>{" "}
            will be requested again from the provider on next use.
          </p>
          <p className="mt-2">
            This new request is <strong>billed</strong>. Only do this if
            the current record is wrong or outdated, or if the track was
            just added to the reference database.
          </p>
        </ConfirmModal>
      )}
    </Page>
  );
}
